<template>
  <div class="pa-6">
    <Loader :messageSelector="messageSelector" :overlay="overlay" />
    <v-form ref="form" v-model="valid" :disabled="readonly">
      <fieldset>
        <legend class="text-h6 text-sm-h5">Déclarations obligatoires</legend>
        <!-- Info bulle -->
        <v-alert text color="secondary" type="info">
          <p>Vous avez l'obligation d'aviser l'Ordre, si vous avez fait l'objet :</p>
          <ul class="ml-5">
            <li>de certaines décisions disciplinaires <span class="font-weight-bold">d'organismes autres que l'OTSTCFQ;</span></li>
            <li>
              d'une décision judiciaire vous déclarant coupable d'une infraction criminelle, qu'elle soit ou non en lien avec votre
              profession;
            </li>
            <li>de certaines décisions pénales en lien avec le Code des professions ou;</li>
            <li>d'une poursuite pour une infraction punissable de cinq ans d'emprisonnement ou plus.</li>
          </ul>
          <div class="font-weight-bold mt-5">
            Veuillez
            <span class="text-decoration-underline">uniquement</span> déclarer les nouvelles décisions ou poursuites que vous n'avez pas
            déjà déclarées à l'Ordre.
          </div>
        </v-alert>

        <!-- Decision disciplinaire -->
        <p class="text-h4 text-sm-h4 sub-title">Décision disciplinaire</p>
        <div class="legalSection">
          <p class="mentionLegal">
            <v-alert text color="orange" type="error">
              Si vous avez fait l'objet d'une décision disciplinaire d'une autre organisation vous ayant imposé une sanction depuis votre
              demande de délivrance de permis ou dans votre dernière demande d'inscription ou de renouvellement, vous devez en informer
              l'Ordre en remplissant les questions ci-dessous.
            </v-alert>
          </p>
          <!-- On désactive en attendant l'import des déclarations des années précédentes -->
          <v-checkbox
              v-if="activerAucunChangement"
              v-model="getDeclarationsObligatoires.pasDeChangement"
              class="li"
              color="primary"
              hide-details
              label="Aucun changement par rapport à l'information fournie dans ma demande de délivrance de permis ou dans ma dernière demande d'inscription ou de renouvellement"
          ></v-checkbox>
          <div v-if="!getDeclarationsObligatoires.pasDeChangement">
            <p class="mentionLegal">
              J'ai fait l'objet d'une décision disciplinaire d'un autre ordre professionnel du Québec (autre que l'OTSTCFQ) ou du Tribunal
              des professions m'ayant imposé une limitation ou une suspension de mon droit d'exercer des activités professionnelles ou une
              radiation du tableau de l'ordre concerné ou la révocation de mon permis.
            </p>
            <v-autocomplete
                v-model="getDeclarationsObligatoires.decisionDisciplinaireDUnAutreOrdreProfessionnel"
                :item-text="itemText"
                :item-value="itemValue"
                :items="listesDecisionDisciplinaireAutreOrdre"
                :rules="rules.DecisionDisciplinaireDUnAutreOrdreProfessionnel"
                class="cssRequired"
                label="Réponse"
                required
                return-object
                @change="disciplinaireChange"
            ></v-autocomplete>
            <p class="mentionLegal">
              J'ai fait l'objet d'une décision disciplinaire d'un organisme de l'extérieur du Québec (autre province ou territoire canadien
              ou autre pays) ayant le même effet qu'une limitation ou une suspension du droit d'exercice, qu'une radiation du tableau de
              l'Ordre ou qu'une révocation de permis
            </p>
            <v-autocomplete
                v-model="getDeclarationsObligatoires.decisionDisciplinaireDUnOrganismeDeLExterieurDuQuebec"
                :item-text="itemText"
                :item-value="itemValue"
                :items="listesDecisionDisciplinaireAutreOrdre"
                :rules="rules.DecisionDisciplinaireDUnOrganismeDeLExterieurDuQuebec"
                class="cssRequired"
                label="Réponse"
                required
                return-object
                @change="disciplinaireChange"
            ></v-autocomplete>
          </div>
        </div>

        <!-- Compléments décisions disciplinaires -->
        <fieldset v-if="disciplinaire">
          <legend class="text-h6 text-sm-h5">Compléments décisions disciplinaires</legend>
          <!-- Nature -->
          <v-text-field
              v-model="getDeclarationsObligatoires.natureDeLInfraction"
              :rules="rules.NatureDeLInfraction"
              class="cssRequired"
              label="Nature de l'infraction"
              required
          ></v-text-field>
          <!-- Nature -->
          <v-text-field
              v-model="getDeclarationsObligatoires.natureDeLaSanction"
              :rules="rules.NatureDeLaSanction"
              class="cssRequired"
              label="Nature de la sanction"
              required
          ></v-text-field>
          <!-- Date de la decision -->
          <v-menu
              ref="menuDateDecision"
              v-model="dateDecisionMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              :rules="rules.DateDecision"
              autocomplete="off"
              class="cssRequired"
              min-width="290px"
              offset-y
              required
              transition="scale-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-sheet max-width="200">
                <v-text-field
                    v-model="getDeclarationsObligatoires.dateDecision"
                    v-mask="'####-##-##'"
                    :rules="rules.DateDecision"
                    append-icon="mdi-calendar"
                    class="cssRequired"
                    hint="Format YYYY-MM-DD"
                    label="Date de la décision"
                    readonly
                    required
                    v-bind="attrs"
                    v-on="on"
                />
              </v-sheet>
            </template>
            <v-date-picker
                ref="pickerDateDecision"
                v-model="getDeclarationsObligatoires.dateDecision"
                :max="new Date().toISOString().substring(0, 10)"
                :rules="rules.DateDecision"
                required
                @change="saveDateDecision"
            />
          </v-menu>
          <!-- Nom organisme -->
          <v-text-field
              v-model="getDeclarationsObligatoires.nomOrganisme"
              :rules="rules.NomOrganisme"
              class="cssRequired"
              label="Nom de l'organisme qui a rendu la décision"
              required
          ></v-text-field>
          <!-- Province/Pays -->
          <v-text-field
              v-model="getDeclarationsObligatoires.provincePays"
              :rules="rules.ProvincePays"
              class="cssRequired"
              label="Province/Pays"
              required
          ></v-text-field>
          <!-- Num Permis -->
          <v-text-field
              v-model="getDeclarationsObligatoires.nPermisDeCetOrdre"
              :rules="rules.NPermisDeCetOrdre"
              class="cssRequired"
              label="N° de permis délivré par cet ordre ou organisme professionnel"
              required
          ></v-text-field>
          <v-col class="legalSection">
            <Attachment
                ref="disciplinaireAttachment"
                file-prefix="disciplinaire"
                :min-required="1"
                @completeEvent="saveNextAttachment"
                @refreshEvent="validateForm(true)"
            ></Attachment>
          </v-col>
        </fieldset>

        <!-- Décisions judiciaires -->
        <p class="text-h4 text-sm-h4 sub-title">Décisions judiciaires</p>
        <div class="legalSection">
          <p class="mentionLegal">
            <v-alert text color="orange" type="error">
              Si vous avez déjà fait l'objet d'un jugement d'un tribunal canadien (incluant un tribunal situé au Québec) ou étranger vous
              déclarant coupable d'une infraction criminelle ou d'une infraction pénale en vertu du Code des professions depuis votre
              demande de délivrance de permis ou dans votre dernière demande d'inscription ou de renouvellement, vous devez en informer
              l'Ordre en remplissant les questions ci-dessous.
            </v-alert>
          </p>
          <v-checkbox
              v-if="activerAucunChangement && demandeAdmissionId"
              v-model="getDeclarationsObligatoires.pasDeChangementJudiciaire"
              class="li"
              color="primary"
              hide-details
              label="Aucun changement par rapport à l'information fournie dans ma demande de délivrance de permis ou dans ma dernière demande d'inscription ou de renouvellement"
          ></v-checkbox>
          <div v-if="!getDeclarationsObligatoires.pasDeChangementJudiciaire">
            <p class="mentionLegal">
              J'ai fait l'objet d'une décision d'un tribunal canadien ou étranger me déclarant coupable d'une infraction criminelle.
            </p>
            <v-autocomplete
                v-model="getDeclarationsObligatoires.decisionDUnTribunalCanadienOuEtranger"
                :item-text="itemText"
                :item-value="itemValue"
                :items="listeDecisionTribunalCanadienOuEtranger"
                :rules="rules.DecisionDUnTribunalCanadienOuEtranger"
                class="cssRequired"
                label="Réponse"
                required
                return-object
                @change="accusationChange"
            ></v-autocomplete>
            <p class="mentionLegal">
              J'ai fait l'objet d'une décision rendue au Québec me déclarant coupable d'une infraction pénale pour avoir contrevenu à une
              disposition du Code des professions (en vertu de l'article 188 de ce Code).
            </p>
            <v-autocomplete
                v-model="getDeclarationsObligatoires.decisionRendueAuQuebec"
                :item-text="itemText"
                :item-value="itemValue"
                :items="listesDecisionRenduAuQuebec"
                :rules="rules.DecisionRendueAuQuebec"
                class="cssRequired"
                label="Réponse"
                required
                return-object
                @change="accusationChange"
            ></v-autocomplete>
          </div>
        </div>

        <!-- Compléments décisions judiciaires -->
        <fieldset v-if="accusation">
          <legend class="text-h6 text-sm-h5">Compléments décisions judiciaires</legend>
          <!-- Object accusation -->
          <v-text-field
              v-model="getDeclarationsObligatoires.objetDeLAccusation"
              :rules="rules.ObjetDeLAccusation"
              class="cssRequired"
              label="Décrire l'objet de l'accusation"
              required
          ></v-text-field>
          <!-- Pays -->
          <v-text-field
              v-model="getDeclarationsObligatoires.pays"
              :rules="rules.Pays"
              class="cssRequired"
              label="Pays"
              required
          ></v-text-field>
          <!-- Province -->
          <v-text-field
              v-model="getDeclarationsObligatoires.provinceEtat"
              :rules="rules.ProvinceEtat"
              class="cssRequired"
              label="Province ou État"
              required
          ></v-text-field>
          <!-- District judiciaire -->
          <v-text-field
              v-model="getDeclarationsObligatoires.districtJudiciaire"
              :rules="rules.DistrictJudiciaire"
              class="cssRequired"
              label="District judiciaire"
              required
          ></v-text-field>
          <!-- Num Dossier -->
          <v-text-field
              v-model="getDeclarationsObligatoires.numeroDeDossierDeLaCour"
              :rules="rules.NumeroDeDossierDeLaCour"
              class="cssRequired"
              label="N° de dossier de la Cour"
              required
          ></v-text-field>
          <section>
            <div class="mentionLegal">
              <h3 class="decisionJudiciaireDocumentRequis">Fournir en plus les documents suivants :</h3>
              <ol class="k">
                <li>Acte d'accusation ou dénonciation</li>
                <li>Jugement ou procès-verbal de la condamnation</li>
                <li>Preuve transmise par le poursuivant dans le cadre de son obligation de communication de la preuve, le cas échéant</li>
                <li>
                  Rapport présentenciel ou prédécisionnel,
                  <span class="text-decoration-underline">s'il y en a un</span>
                </li>
                <li>Une lettre détaillant les circonstances de l'événement.</li>
              </ol>
            </div>

            <!-- Seulement si la personne a eu un pardon -->
            <v-col class="legalSection">
              <Attachment
                  ref="decisionJudiciaireAttachment"
                  file-prefix="judiciaire"
                  :min-required="1"
                  @completeEvent="saveNextAttachment"
                  @refreshEvent="validateForm(true)"
              ></Attachment>
            </v-col>
          </section>
        </fieldset>

        <!-- Poursuites judiciaires -->
        <p class="text-h4 text-sm-h4 sub-title">Poursuites judiciaires</p>
        <div class="legalSection">
          <v-alert text color="orange" type="error">
            Si vous faites l'objet de toute poursuite pour une infraction punissable de 5 ans ou plus d'emprisonnement depuis votre demande
            de délivrance de permis ou dans votre dernière demande d'inscription ou de renouvellement, vous devez en informer l'Ordre en
            remplissant les questions ci-dessous, et ce, conformément à l'article 59.3 du Code des professions.
          </v-alert>
          <v-checkbox
              v-if="activerAucunChangement && demandeAdmissionId"
              v-model="getDeclarationsObligatoires.pasDeChangementInfraction"
              class="li"
              color="primary"
              hide-details
              label="Aucun changement par rapport à l'information fournie dans ma demande de délivrance de permis ou dans ma dernière demande d'inscription ou de renouvellement"
          ></v-checkbox>
          <div v-if="!getDeclarationsObligatoires.pasDeChangementInfraction">
            <p class="mentionLegal">
              Je fais ou j'ai fait l'objet de poursuite pour une infraction punissable de 5 ans ou plus d'emprisonnement au Canada ou à
              l'étranger.
            </p>
            <v-autocomplete
                v-model="getDeclarationsObligatoires.poursuitePourUneInfractionPunissableDe5AnsOuPlusDEmprisonnement"
                :item-text="itemText"
                :item-value="itemValue"
                :items="listesPoursuiteInfractionsPunissables"
                :rules="rules.PoursuitePourUneInfractionPunissableDe5AnsOuPlusDEmprisonnement"
                class="cssRequired"
                label="Réponse"
                required
                return-object
                @change="infractionChange"
            ></v-autocomplete>
          </div>
        </div>

        <!-- Compléments poursuites judiciaires -->
        <fieldset v-if="infraction">
          <legend class="text-h6 text-sm-h5">Compléments poursuites judiciaires</legend>
          <!-- Object accusation -->
          <v-text-field
              v-model="getDeclarationsObligatoires.objetAccusation"
              :rules="rules.ObjetAccusation"
              class="cssRequired"
              label="Décision de l'objet de l'accusation"
              required
          ></v-text-field>
          <!-- Pays -->
          <v-text-field
              v-model="getDeclarationsObligatoires.pays2"
              :rules="rules.Pays2"
              class="cssRequired"
              label="Pays"
              required
          ></v-text-field>
          <!-- Province -->
          <v-text-field
              v-model="getDeclarationsObligatoires.provinceEtat2"
              :rules="rules.ProvinceEtat2"
              class="cssRequired"
              label="Province ou État"
              required
          ></v-text-field>
          <!-- Num Dossier -->
          <v-text-field
              v-model="getDeclarationsObligatoires.nDossierDeLaCour"
              :rules="rules.NDossierDeLaCour"
              class="cssRequired"
              label="N° de dossier de la Cour"
              required
          ></v-text-field>
          <div class="mentionLegal">
            <h3 class="decisionJudiciaireDocumentRequis">Fournir en plus les documents suivants :</h3>
            <ol class="k">
              <li>Acte d'accusation ou dénonciation</li>
              <li>Preuve transmise par le poursuivant dans le cadre de son obligation de communication de la preuve, le cas échéant</li>
              <li>
                Rapport présentenciel ou prédécisionnel,
                <span class="text-decoration-underline">s'il y en a un</span>
              </li>
            </ol>
          </div>
          <v-col class="legalSection">
            <Attachment
                ref="poursuiteJudiciaireAttachment"
                file-prefix="decision"
                :min-required="1"
                @completeEvent="saveNextAttachment"
                @refreshEvent="validateForm(true)"
            ></Attachment>
          </v-col>
        </fieldset>
      </fieldset>
    </v-form>

    <!-- Buttons -->
    <div class="text-center mt-6 d-flex justify-space-between">
      <v-btn color="primary" @click="back()">Retour</v-btn>
      <v-btn :disabled="!valid" color="secondary" @click="saveAndContinueLater()"> Enregistrer et terminer plus tard </v-btn>
      <v-btn :disabled="!valid" color="primary" @click="saveAndContinue()">Suivant</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import Attachment from "../attachments/Attachment"
import Loader from "../Loader"
import NavigationPageFormulaireMixin from "@/mixins/navigationPageFormulaireMixin"
import PageReadonlyFormulaireAdmissionMixin from "@/mixins/pageReadonlyFormulaireAdmissionMixin"
import AttachementFormulaireMixin from "@/mixins/attachementFormulaireMixin"

export default {
  mixins: [
    NavigationPageFormulaireMixin,
    PageReadonlyFormulaireAdmissionMixin,
    AttachementFormulaireMixin,
  ],
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  components: {
    Attachment: Attachment,
    Loader: Loader,
  },
  data() {
    return {
      messageSelector: "",
      activerAucunChangement: false,
      overlay: false,
      valid: false,
      dateDecisionMenu: false,
      rules: {
        DecisionDisciplinaireDUnAutreOrdreProfessionnel: [(v) => !!v || "Cette valeur est requise"],
        DecisionDisciplinaireDUnOrganismeDeLExterieurDuQuebec: [(v) => !!v || "Cette valeur est requise"],
        NatureDeLInfraction: [(v) => !!v || "La nature de l'infraction est obligatoire"],
        NatureDeLaSanction: [(v) => !!v || "La nature de la sanction est obligatoire"],
        DateDecision: [(v) => !!v || "La date de la décision est obligatoire"],
        NomOrganisme: [(v) => !!v || "Le nom de l'organisme est obligatoire"],
        ProvincePays: [(v) => !!v || "La province et le pays sont obligatoire"],
        NPermisDeCetOrdre: [(v) => !!v || "Le n° de permis délivré est obligatoire"],
        DecisionDUnTribunalCanadienOuEtranger: [(v) => !!v || "Cette valeur est requise"],
        DecisionRendueAuQuebec: [(v) => !!v || "Cette valeur est requise"],
        ObjetDeLAccusation: [(v) => !!v || "L'objet d'accusation est requis"],
        Pays: [(v) => !!v || "Le pays est requis"],
        ProvinceEtat: [(v) => !!v || "La province est requise"],
        DistrictJudiciaire: [(v) => !!v || "Le district judiciaire est requis"],
        NumeroDeDossierDeLaCour: [(v) => !!v || "Le numéro de dossier de la cour est requis"],
        ObjetAccusation: [(v) => !!v || "L'objet d'accusation est requis"],
        Pays2: [(v) => !!v || "Le pays est requis"],
        ProvinceEtat2: [(v) => !!v || "La Province/État est requis"],
        NDossierDeLaCour: [(v) => !!v || "Le numéro de dossier de la cour est requis"],
        PoursuitePourUneInfractionPunissableDe5AnsOuPlusDEmprisonnement: [(v) => !!v || "Cette valeur est requise"],
      },
    }
  },
  computed: {
    ...mapGetters({
      demandeAdmissionId: "DemandeAdmission/GET_DEMANDEADMISSIONID",
      getDeclarationsObligatoires: "DeclarationsObligatoires/GET_DECLARATIONSOBLIGATOIRES",
      listesDecisionDisciplinaireAutreOrdre: "Catalogue/GET_DECISIONDISCIPLINAIREAUTREORDRE",
      listeDecisionTribunalCanadienOuEtranger: "Catalogue/GET_DECISIONTRIBUNALCANADIENOUETRANGER",
      listesDecisionRenduAuQuebec: "Catalogue/GET_DECISIONRENDUAUQUERBEC",
      listesPoursuiteInfractionsPunissables: "Catalogue/GET_POURSUITEINFRACTIONPUNISSABLES",
    }),
    disciplinaire: function () {
      return (
          this.getDeclarationsObligatoires &&
          ((this.getDeclarationsObligatoires.decisionDisciplinaireDUnAutreOrdreProfessionnel &&
                  this.getDeclarationsObligatoires.decisionDisciplinaireDUnAutreOrdreProfessionnel.lang00 === "Oui") ||
              (this.getDeclarationsObligatoires.decisionDisciplinaireDUnOrganismeDeLExterieurDuQuebec &&
                  this.getDeclarationsObligatoires.decisionDisciplinaireDUnOrganismeDeLExterieurDuQuebec.lang00 === "Oui"))
      )
    },
    accusation: function () {
      return (
          this.getDeclarationsObligatoires &&
          ([
                this.$catalogues.declarationObligatoireDesicisionTribunal.pardon,
                this.$catalogues.declarationObligatoireDesicisionTribunal.oui,
              ].includes(this.getDeclarationsObligatoires.decisionDUnTribunalCanadienOuEtranger?.id) ||
              this.getDeclarationsObligatoires.decisionRendueAuQuebec?.lang00 === "Oui")
      )
    },
    infraction: function () {
      return (
          this.getDeclarationsObligatoires &&
          this.getDeclarationsObligatoires.poursuitePourUneInfractionPunissableDe5AnsOuPlusDEmprisonnement &&
          this.getDeclarationsObligatoires.poursuitePourUneInfractionPunissableDe5AnsOuPlusDEmprisonnement.lang00 === "Oui"
      )
    },
  },
  created() {
    this.fetchItems()
  },
  watch: {
    dateDecisionMenu(val) {
      val && setTimeout(() => (this.$refs.pickerDateDecision.activePicker = "YEAR"))
    },
  },
  methods: {
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      updateDeclarationObligatoires: "DeclarationsObligatoires/updateDeclarationsObligatoires",
      createDeclarationObligatoires: "DeclarationsObligatoires/createDeclarationsObligatoires",
      getDeclarationObligatoiresByDemandeAdmissionId: "DeclarationsObligatoires/getDeclarationsObligatoiresByDemandeAdmissionId",
      getDocumentsByDemandeAdmissionId: "Documents/getDocumentsByDemandeAdmissionId",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.decisionDisciplinaireAutreOrdre),
          this.getCatalogue(this.$cataloguesDesc.decisionTribunalCanadienOuEtranger),
          this.getCatalogue(this.$cataloguesDesc.decisionRenduAuQuebec),
          this.getCatalogue(this.$cataloguesDesc.poursuiteInfractionsPunissables),
        ]

        if (this.demandeAdmissionId) {
          requests.push(this.getDeclarationObligatoiresByDemandeAdmissionId(this.demandeAdmissionId))
          requests.push(this.getDocumentsByDemandeAdmissionId(this.demandeAdmissionId))
        }

        await Promise.all(requests)

        // Liste les uploads de fichiers
        this.refsUpload = [
          () => this.$refs.disciplinaireAttachment,
          () => this.$refs.decisionJudiciaireAttachment,
          () => this.$refs.poursuiteJudiciaireAttachment,
        ]

        this.validateForm(true)
        this.overlay = false
      } catch (exception) {
        console.log("Exception", exception)
      }
    },
    async save() {
      this.validateForm()

      if (this.valid) {
        this.messageSelector = "Save"
        this.overlay = true

        if (this.demandeAdmissionId) {
          this.getDeclarationsObligatoires.demandeadmission = this.demandeAdmissionId
        }

        if (this.getDeclarationsObligatoires.id) {
          await this.updateDeclarationObligatoires(this.getDeclarationsObligatoires)
        } else {
          await this.createDeclarationObligatoires(this.getDeclarationsObligatoires)
        }

        this.saveAttachments("demandeAdmission", this.demandeAdmissionId, () => {
          this.overlay = false
          this.redirect()
        })
      }
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    validateForm(resetValidation = false) {
      this.valid = (this.$refs.form?.validate() || false)
          && !!this.getDeclarationsObligatoires

      if (resetValidation) {
        this.$refs.form?.resetValidation()
      }
    },
    saveDateDecision(date) {
      this.$refs.menuDateDecision.save(date)
    },
    disciplinaireChange() {
      if (this.disciplinaire === false) {
        this.getDeclarationsObligatoires.natureDeLInfraction = null
        this.getDeclarationsObligatoires.natureDeLaSanction = null
        this.getDeclarationsObligatoires.dateDecision = null
        this.getDeclarationsObligatoires.nomOrganisme = null
        this.getDeclarationsObligatoires.provincePays = null
        this.getDeclarationsObligatoires.nPermisDeCetOrdre = null
        this.$refs.disciplinaireAttachment?.removeAllTempFile()

        this.validateForm(true)
      }
    },
    accusationChange() {
      if (this.accusation === false) {
        this.getDeclarationsObligatoires.objetDeLAccusation = null
        this.getDeclarationsObligatoires.pays = null
        this.getDeclarationsObligatoires.provinceEtat = null
        this.getDeclarationsObligatoires.districtJudiciaire = null
        this.getDeclarationsObligatoires.numeroDeDossierDeLaCour = null
        this.$refs.decisionJudiciaireAttachment?.removeAllTempFile()

        this.validateForm(true)
      }
    },
    infractionChange() {
      if (this.infraction === false) {
        this.getDeclarationsObligatoires.objetAccusation = null
        this.getDeclarationsObligatoires.pays2 = null
        this.getDeclarationsObligatoires.provinceEtat2 = null
        this.getDeclarationsObligatoires.nDossierDeLaCour = null
        this.$refs.poursuiteJudiciaireAttachment?.removeAllTempFile()

        this.validateForm(true)
      }
    },
  },
}
</script>

<style scoped>
.decisionJudiciaireDocumentRequis {
  font-weight: bold;
  text-decoration: underline;
}
</style>